import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { apiGet, apiPatch, apiPost } from '../../lib/api';

/**
 * Clienții, dinspre client.
 *
 * Schemele Zod sunt declarate aici, nu importate din `@pcrm/be/customers`: `apps/crm` e
 * `scope:fe` și n-are voie să depindă de backend. Aceeași alegere ca la `companies.api.ts` —
 * granița se validează în ambele capete, iar un câmp redenumit pe server pică la `parse`, nu
 * ajunge `undefined` în formular.
 */

/**
 * Felurile de client. Numerele sunt cele din `crm_customers.customer_type`.
 *
 * Aici e **sursa de adevăr a interfeței**: eticheta felului și cum se numește la el câmpul
 * de identificare fiscală. Formularul citește din tabelul ăsta în loc să înșire `if`-uri —
 * cine adaugă al patrulea fel de client completează o linie, nu caută ramuri prin ecran.
 */
export const CUSTOMER_TYPES = {
  individual: 0,
  company: 1,
  foreign: 2,
} as const;

export type CustomerType = (typeof CUSTOMER_TYPES)[keyof typeof CUSTOMER_TYPES];

export interface CustomerTypeDescriptor {
  readonly value: CustomerType;
  readonly label: string;
  /** Ce scrie pe eticheta câmpului de identificare fiscală. */
  readonly fiscalIdLabel: string;
}

export const CUSTOMER_TYPE_LIST: readonly CustomerTypeDescriptor[] = [
  { value: CUSTOMER_TYPES.individual, label: 'Persoană fizică', fiscalIdLabel: 'CNP' },
  { value: CUSTOMER_TYPES.company, label: 'Persoană juridică', fiscalIdLabel: 'Cod fiscal (CUI)' },
  { value: CUSTOMER_TYPES.foreign, label: 'Firmă străină', fiscalIdLabel: 'Cod fiscal / VAT ID' },
];

export function customerTypeLabel(value: CustomerType): string {
  return CUSTOMER_TYPE_LIST.find((type) => type.value === value)?.label ?? 'Necunoscut';
}

/** `true` doar pentru persoana juridică românească — singura pentru care ANAF are date. */
export function hasAnafLookup(value: CustomerType): boolean {
  return value === CUSTOMER_TYPES.company;
}

const customerTypeSchema = z.union([z.literal(0), z.literal(1), z.literal(2)]);

const customerListItemSchema = z.object({
  id: z.uuid(),
  customerType: customerTypeSchema,
  nickname: z.string(),
  name: z.string(),
  companyId: z.uuid(),
  companyName: z.string(),
  cif: z.string().nullable(),
  city: z.string().nullable(),
  county: z.string().nullable(),
  countryCode: z.string(),
  email: z.string().nullable(),
  phoneSales: z.string().nullable(),
  status: z.boolean(),
  /**
   * Momentul arhivării, `null` = nearhivat. **Nu e `status`**: `status` spune dacă clientul
   * mai lucrează cu noi, arhivarea spune că nu-l mai vrem în listă. Vezi
   * `docs/wiki/domains/companies-and-clients.md#arhivarea`.
   */
  archivedAt: z.string().nullable(),
});

export type CustomerListItem = z.infer<typeof customerListItemSchema>;

/**
 * Fișa întreagă. Are CNP-ul și seria de CI, pe care lista nu le duce.
 *
 * `companyName` trece și aici, ca în listă: e furnizorul din antetul fișei (#222).
 */
const customerSchema = customerListItemSchema.extend({
  clientProfileId: z.uuid().nullable(),
  cnp: z.string().nullable(),
  idCardNumber: z.string().nullable(),
  tradeRegisterNo: z.string().nullable(),
  iban: z.string().nullable(),
  bankName: z.string().nullable(),
  legalAddress: z.string().nullable(),
  postalCode: z.string().nullable(),
  /**
   * `null` = **nu s-a verificat niciodată la ANAF**, nu „nu e plătitor".
   *
   * ⚠️ Trei stări, nu două. Citite ca `boolean`, un client neverificat ar apărea în
   * formular ca neplătitor, iar salvarea ar scrie afirmația aia în bază.
   */
  vatPayer: z.boolean().nullable(),
  vatOnCollection: z.boolean().nullable(),
  representativeName: z.string().nullable(),
  representativeRole: z.string().nullable(),
  contactPerson: z.string().nullable(),
  phoneBilling: z.string().nullable(),
  billingEmail: z.string().nullable(),
  phoneTechnical: z.string().nullable(),
  phoneOther: z.string().nullable(),
  notes: z.string().nullable(),
  autoInvoicing: z.boolean(),
  detailedInvoice: z.boolean(),
  isSpecial: z.boolean(),
  bankStatementNames: z.string().nullable(),
});

export type Customer = z.infer<typeof customerSchema>;

/**
 * Ce trimite formularul.
 *
 * **Totul e text**, inclusiv acolo unde serverul așteaptă altceva: un `<input>` gol e `''`,
 * nu `null`, iar contractul de pe server știe deja să facă diferența. Cifrele de control
 * (CNP, CUI, IBAN) sunt tot ale serverului — o a doua implementare în browser ar putea da
 * alt verdict decât cea care contează.
 */
export interface CustomerInput {
  readonly customerType: CustomerType;
  readonly companyId: string;
  readonly countryCode: string;
  readonly nickname: string;
  readonly name: string;
  readonly clientProfileId: string;
  readonly cnp: string;
  readonly cif: string;
  readonly idCardNumber: string;
  readonly tradeRegisterNo: string;
  readonly iban: string;
  readonly bankName: string;
  readonly legalAddress: string;
  readonly city: string;
  readonly county: string;
  readonly postalCode: string;
  /** `''` = necunoscut, `'da'` / `'nu'` = răspuns. Vezi `TRISTATE_OPTIONS`. */
  readonly vatPayer: string;
  readonly vatOnCollection: string;
  readonly representativeName: string;
  readonly representativeRole: string;
  readonly contactPerson: string;
  readonly email: string;
  readonly phoneSales: string;
  readonly phoneBilling: string;
  readonly billingEmail: string;
  readonly phoneTechnical: string;
  readonly phoneOther: string;
  readonly notes: string;
  readonly autoInvoicing: boolean;
  readonly detailedInvoice: boolean;
  readonly isSpecial: boolean;
  readonly bankStatementNames: string;
  readonly status: boolean;
}

export interface CustomerFilters {
  readonly search?: string;
  readonly companyId?: string;
  readonly customerType?: CustomerType;
  readonly status?: 'active' | 'inactive';
  /**
   * Arhivații: lipsă = doar nearhivații · `all` = și unii, și alții · `only` = doar arhivații.
   *
   * Ecranul trimite valoarea **explicit**, nu se bazează pe implicitul serverului: comutatorul
   * „Arată și arhivații" pune `all`, oprit nu pune nimic. Ecranul decide ce vrea să vadă.
   */
  readonly archived?: 'all' | 'only';
  readonly page: number;
}

export const CUSTOMERS_PAGE_SIZE = 25;

export const CUSTOMERS_QUERY_KEY = ['customers'] as const;

export const customerQueryKey = (id: string) => ['customers', id] as const;

function toSearchParams(filters: CustomerFilters): string {
  const params = new URLSearchParams();

  if (filters.search) params.set('search', filters.search);
  if (filters.companyId) params.set('companyId', filters.companyId);
  if (filters.customerType !== undefined) {
    params.set('customerType', String(filters.customerType));
  }
  if (filters.status) params.set('status', filters.status);
  if (filters.archived) params.set('archived', filters.archived);
  params.set('page', String(filters.page));
  params.set('perPage', String(CUSTOMERS_PAGE_SIZE));

  return params.toString();
}

export const customersQueryOptions = (filters: CustomerFilters) =>
  queryOptions({
    queryKey: [...CUSTOMERS_QUERY_KEY, filters] as const,
    queryFn: () =>
      apiGet(
        `/customers?${toSearchParams(filters)}`,
        z.object({
          items: z.array(customerListItemSchema),
          total: z.number(),
          page: z.number(),
          perPage: z.number(),
        }),
      ),
  });

export const customerDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: customerQueryKey(id),
    queryFn: () => apiGet(`/customers/${id}`, z.object({ customer: customerSchema })),
  });

/**
 * Ce se trimite spre server: doar câmpurile felului de client ales.
 *
 * Curățarea se face **aici**, nu în formular: formularul păstrează ce a tastat operatorul
 * dacă tot comută între tipuri, iar contractul de pe server taie oricum ce nu se aplică.
 * Ce câștigăm trimițând puțin: corpul cererii se citește ca fișa care se salvează, nu ca
 * starea formularului — util când cineva se uită în consolă și se întreabă de ce pleacă un
 * CNP pentru o firmă.
 */
function toPayload(input: CustomerInput): Record<string, unknown> {
  const common = {
    customerType: input.customerType,
    companyId: input.companyId,
    nickname: input.nickname,
    name: input.name,
    clientProfileId: input.clientProfileId,
    iban: input.iban,
    bankName: input.bankName,
    legalAddress: input.legalAddress,
    city: input.city,
    postalCode: input.postalCode,
    contactPerson: input.contactPerson,
    email: input.email,
    phoneSales: input.phoneSales,
    phoneBilling: input.phoneBilling,
    billingEmail: input.billingEmail,
    phoneTechnical: input.phoneTechnical,
    phoneOther: input.phoneOther,
    notes: input.notes,
    autoInvoicing: input.autoInvoicing,
    detailedInvoice: input.detailedInvoice,
    isSpecial: input.isSpecial,
    bankStatementNames: input.bankStatementNames,
    status: input.status,
  };

  if (input.customerType === CUSTOMER_TYPES.individual) {
    return { ...common, cnp: input.cnp, idCardNumber: input.idCardNumber, county: input.county };
  }

  const legalEntity = {
    ...common,
    cif: input.cif,
    tradeRegisterNo: input.tradeRegisterNo,
    representativeName: input.representativeName,
    representativeRole: input.representativeRole,
    // TVA-ul e doar la persoane juridice: o persoană fizică nu e înregistrată în scopuri
    // de TVA, iar serverul respinge câmpurile trimise unde n-au ce căuta.
    vatPayer: fromTristate(input.vatPayer),
    vatOnCollection: fromTristate(input.vatOnCollection),
  };

  return input.customerType === CUSTOMER_TYPES.company
    ? { ...legalEntity, county: input.county }
    : { ...legalEntity, countryCode: input.countryCode };
}

/**
 * „Nu se știe", ca valoare de formular.
 *
 * ⚠️ **Un cuvânt, nu șirul gol.** `Select.Item` din ui-kit (Radix) **refuză** `value=""`:
 * acolo șirul gol înseamnă „fără selecție, arată placeholder-ul", deci componenta aruncă la
 * randare. Iar aici absența e o valoare cu nume — a treia stare, nu lipsa unui răspuns.
 * Găsit pe viu, la deschiderea fișei (fondatorul, 2026-09-16).
 */
export const TRISTATE_UNKNOWN = 'necunoscut';

/**
 * Cele trei stări, ca opțiuni de select.
 *
 * O bifă n-ar putea exprima „nu se știe", iar aia e chiar starea implicită: clienții
 * existenți n-au fost verificați la ANAF.
 */
export const TRISTATE_OPTIONS = [
  { value: TRISTATE_UNKNOWN, label: 'Nu se știe' },
  { value: 'da', label: 'Da' },
  { value: 'nu', label: 'Nu' },
] as const;

/**
 * Valoarea din formular → ce pleacă spre server.
 *
 * ⚠️ Nu `Boolean(value)` și nici `value === 'da'` singur: amândouă trimit `false` pentru
 * „nu se știe", adică scriu „neplătitor de TVA" despre un client pe care nu l-a verificat
 * nimeni.
 */
function fromTristate(value: string): boolean | null {
  if (value === 'da') {
    return true;
  }

  return value === 'nu' ? false : null;
}

/** `boolean | null` → valoarea din formular. Perechea lui `fromTristate`. */
export function toTristate(value: boolean | null): string {
  return value === null ? TRISTATE_UNKNOWN : value ? 'da' : 'nu';
}

export function createCustomer(input: CustomerInput): Promise<Customer> {
  return apiPost('/customers', toPayload(input), z.object({ customer: customerSchema })).then(
    (body) => body.customer,
  );
}

/**
 * Ce se închide odată cu clientul.
 *
 * ⚠️ **Stările sunt `z.number()`, nu `z.enum`.** Un frontend rămas în urmă cu un deploy ar
 * face **tot** răspunsul să pice la `parse` pentru o valoare nouă — adică sertarul de
 * arhivare ar refuza să se deschidă din cauza unei etichete. Necunoscutul cade pe descriptorul
 * implicit, ca la iconul tipului de pachet.
 */
const customerArchivePlanSchema = z.object({
  subscriptions: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      address: z.string().nullable(),
      status: z.number(),
    }),
  ),
  documents: z.array(
    z.object({
      id: z.uuid(),
      /** `null` = **ciornă**: act neemis, care se șterge, nu se anulează (#142). */
      documentNumber: z.number().nullable(),
      documentTypeName: z.string(),
      documentState: z.number(),
    }),
  ),
  signing: z.array(
    z.object({
      documentId: z.uuid(),
      /** `null` mereu, în practică: ce pleacă la semnat e o ciornă (#142). */
      documentNumber: z.number().nullable(),
      documentTypeName: z.string(),
      sentToEmail: z.string().nullable(),
      expiresAt: z.string(),
    }),
  ),
});

export type CustomerArchivePlan = z.infer<typeof customerArchivePlanSchema>;

/** Ce arată sertarul cât timp planul nu s-a încărcat, și pentru un client deja arhivat. */
export const EMPTY_ARCHIVE_PLAN: CustomerArchivePlan = {
  subscriptions: [],
  documents: [],
  signing: [],
};

export const customerArchivePlanQueryKey = (id: string) =>
  ['customers', id, 'archive-plan'] as const;

/**
 * Ce s-ar închide dacă s-ar arhiva acum.
 *
 * Se cere **doar când se deschide sertarul**, nu odată cu fișa: e o fotografie a trei
 * tabele, iar o fișă deschisă ca să se citească un telefon n-are ce face cu ea.
 */
export const customerArchivePlanQueryOptions = (id: string) =>
  queryOptions({
    queryKey: customerArchivePlanQueryKey(id),
    queryFn: () =>
      apiGet(`/customers/${id}/archive-plan`, z.object({ plan: customerArchivePlanSchema })).then(
        (body) => body.plan,
      ),
    // Fără cache între deschideri: între două apăsări se poate semna un act sau se poate
    // rezilia un serviciu, iar un sertar care ar promite o listă veche ar minți exact în
    // locul în care cere confirmare.
    staleTime: 0,
    gcTime: 0,
  });

/**
 * Arhivarea și dezarhivarea, ca acțiuni separate de salvarea fișei.
 *
 * Corpul e gol: verbul e chiar ruta. Un `{ archived: true }` ar fi lăsat loc unei a treia
 * valori care nu există.
 *
 * ⚠️ **Arhivarea cascadează** (fondatorul, 2026-09-15): clientul iese inactiv, serviciile în
 * derulare se reziliază, documentele nesemnate se anulează și linkul viu se invalidează.
 * Răspunsul poartă `plan` — ce s-a închis **de fapt**, nu ce promisese sertarul.
 *
 * **Dezarhivarea nu desface nimic**: scoate `archived_at`, atât.
 */
export function archiveCustomer(id: string): Promise<{
  customer: Customer;
  plan: CustomerArchivePlan;
}> {
  return apiPost(
    `/customers/${id}/archive`,
    {},
    z.object({ customer: customerSchema, plan: customerArchivePlanSchema }),
  );
}

export function unarchiveCustomer(id: string): Promise<{
  customer: Customer;
  plan: CustomerArchivePlan;
}> {
  return apiPost(
    `/customers/${id}/unarchive`,
    {},
    z.object({ customer: customerSchema, plan: customerArchivePlanSchema }),
  );
}

export function updateCustomer(id: string, input: CustomerInput): Promise<Customer> {
  return apiPatch(
    `/customers/${id}`,
    toPayload(input),
    z.object({ customer: customerSchema }),
  ).then((body) => body.customer);
}
